import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import CountBadge from '../../components/notifications/CountBadge';
import DashboardThemeToggle from '../../components/dashboard/DashboardThemeToggle';
import TestnetFaucetLink from '../../components/nav/TestnetFaucetLink';
import { useNotificationsOptional } from '../../context/NotificationContext';
import { getJobsPortalItemBadge } from '../../utils/jobsPortalBadges';
import {
    filterJobsPortalMenu,
    getJobsPortalMenuItemName,
    isJobsPortalMenuItemActive,
} from '../../config/jobsPortalNav';
import { useJobsPortalRole } from '../../context/JobsPortalRoleContext';

const SectionLabel = ({ children }) => (
    <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-500">
        {children}
    </p>
);

const navRowClass = (active) =>
    `group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-[14px] font-medium transition-colors duration-200
        ${
            active
                ? 'bg-teal-50 text-teal-900 ring-1 ring-inset ring-teal-200 dark:bg-teal-950/40 dark:text-teal-100 dark:ring-teal-900/60'
                : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-900/70 dark:hover:text-zinc-100'
        }`;

const JobsPortalNavbar = () => {
    const location = useLocation();
    const { role } = useJobsPortalRole();
    const sections = filterJobsPortalMenu(role);
    const notifications = useNotificationsOptional();
    const menuBadges = notifications?.menuBadges ?? {};

    return (
        <aside
            id="jobs-portal-sidebar"
            className="hidden lg:block fixed top-0 left-0 w-64 h-screen z-30"
            aria-label="Jobs workspace sidebar"
        >
            <div className="flex h-full flex-col border-r border-zinc-200 bg-white shadow-[2px_0_12px_rgba(0,0,0,0.04)] dark:border-zinc-800 dark:bg-zinc-950 dark:shadow-[6px_0_18px_rgba(0,0,0,0.5)]">
                <div className="mt-20 flex-1 space-y-6 overflow-y-auto px-3 pb-4">
                    <div>
                        <Link
                            to="/dashboard"
                            className="flex items-center gap-2 rounded-lg px-3 py-2 text-[13px] font-medium text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-900/70 dark:hover:text-zinc-100"
                        >
                            <i className="fa-solid fa-arrow-left text-[12px]" aria-hidden />
                            <span>Back to payments</span>
                        </Link>
                    </div>

                    {sections.map((group) => (
                        <div key={group.label}>
                            <SectionLabel>{group.label}</SectionLabel>
                            <div className="space-y-0.5">
                                {group.items.map((item) => {
                                    const active = isJobsPortalMenuItemActive(
                                        location.pathname,
                                        location.search,
                                        item.id,
                                    );
                                    return (
                                        <Link
                                            key={item.id}
                                            to={item.href}
                                            className={navRowClass(active)}
                                        >
                                            {active && (
                                                <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-teal-600 dark:bg-teal-500" />
                                            )}
                                            <span
                                                className={`inline-flex h-5 w-5 items-center justify-center text-[15px] transition-colors ${
                                                    active
                                                        ? 'text-teal-800 dark:text-teal-300'
                                                        : 'text-zinc-400 group-hover:text-zinc-700 dark:group-hover:text-zinc-200'
                                                }`}
                                            >
                                                <i className={item.icon} />
                                            </span>
                                            <span className="flex-1">
                                                {getJobsPortalMenuItemName(item, role)}
                                            </span>
                                            <CountBadge
                                                value={getJobsPortalItemBadge(
                                                    item.id,
                                                    menuBadges,
                                                )}
                                            />
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="shrink-0 border-t border-zinc-200 px-3 py-4 dark:border-zinc-800">
                    <div className="mb-3 px-1">
                        <TestnetFaucetLink className="w-full justify-center py-2" />
                    </div>
                    <p className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500 dark:text-zinc-500">
                        Appearance
                    </p>
                    <DashboardThemeToggle />
                </div>
            </div>
        </aside>
    );
};

export default JobsPortalNavbar;
